import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "react-i18next";
import "../i18next";

import companyLogo from "../assets/images/Group 2.png";
import companyLogoWhite from "../assets/images/logo-white.png";
import companyLogoColor from "../assets/images/logo_navbar_2.png";
import linkedin from "../assets/images/linkedin8.svg";
import linkedinWhite from "../assets/images/linkedin8white.svg";
import { withLang } from "../utils/langPath";

const SUPPORTED_LANGS = ["es", "en"];

const mapEsToEn = {
  "": "",
  nosotros: "about-us",
  bancos: "banks",
  retail: "retail",
  cajerosautomaticos: "atms",
  servicios: "services",
  contactenos: "contact",
  school: "school",
  blog: "blog",
};
const mapEnToEs = Object.fromEntries(
  Object.entries(mapEsToEn).map(([es, en]) => [en, es]),
);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang: langFromUrl } = useParams(); // "es" | "en"
  const currentLang = SUPPORTED_LANGS.includes(langFromUrl)
    ? langFromUrl
    : "es";

  const { t, i18n } = useTranslation();
  const { setLanguageContext } = useLanguage();

  const [language, setLanguage] = useState(currentLang);
  const [toggleMenu, setToggleMenu] = useState(false);
  const [colorNav, setColorNav] = useState("color");

  const isHome = /^\/(en|es)\/?$/.test(location.pathname);
  const logoSrc = isHome ? companyLogoWhite : companyLogoColor;

  useEffect(() => {
    // Sincroniza select e i18n con la URL
    if (i18n.language !== currentLang) i18n.changeLanguage(currentLang);
    setLanguage(currentLang);
    setLanguageContext(currentLang);
    setColorNav("color");
    window.scrollTo(0, 0);
  }, [currentLang, i18n, setLanguageContext]);

  const mapEsToEn = {
    "": "",
    nosotros: "about-us",
    bancos: "banks",
    retail: "retail",
    cajerosautomaticos: "atms",
    servicios: "services",
    contactenos: "contact",
    school: "school",
    blog: "blog", 
  };

  const mapEnToEs = Object.fromEntries(
    Object.entries(mapEsToEn).map(([es, en]) => [en, es]),
  );

  const changeLanguage = (e) => {
    const nextLang = e.target.value;
    if (nextLang === currentLang) return;

    const segments = location.pathname.split("/").filter(Boolean); // ej: ["es", "blog", "un-slug"]

    let nextPath = "";

    if (segments.length > 0) {
      if (segments[1] === "blog" && segments[2]) {
        const currentSlug = segments[2];
        const blogPosts = require("../data/blogData").blogPosts; 

        const post = blogPosts.find(
          (p) => p.es.slug === currentSlug || p.en.slug === currentSlug,
        );
        const nextSlug = post ? post[nextLang].slug : currentSlug;

        nextPath = `blog/${nextSlug}`;
      }
      else {
        const currentSlug = segments[1] || "";
        nextPath =
          nextLang === "en"
            ? (mapEsToEn[currentSlug] ?? currentSlug)
            : (mapEnToEs[currentSlug] ?? currentSlug);
      }
    }

    i18n.changeLanguage(nextLang);
    setLanguageContext(nextLang);
    setLanguage(nextLang);

    // withLang ya pone la barra diagonal inicial
    navigate(withLang(nextLang, nextPath), { replace: true });
  };

  const temaColor = colorNav === "color";
  const selectStyles = { color: "#19235C" };

  // Slugs por idioma para los Links
  const SLUGS =
    currentLang === "es"
      ? {
          home: "",
          us: "nosotros",
          banks: "bancos",
          retail: "retail",
          atms: "cajerosautomaticos",
          school: "school",
          services: "servicios",
          contact: "contactenos",
          blog: "blog",
        }
      : {
          home: "",
          us: "about-us",
          banks: "banks",
          retail: "retail",
          atms: "atms",
          school: "school",
          services: "services",
          contact: "contact",
          blog: "blog",
        };

  return (
    <nav className="relative p-6 bg-transparent">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <div className="pt-2 w-[30%] md:w-[20%]">
          <Link to={withLang(currentLang, SLUGS.home)}>
            <img src={logoSrc} alt="Company Logo" className="w-32 md:w-40" />
          </Link>
        </div>

        {/* Menu desktop */}
        <div className="hidden space-x-4 md:flex md:text-base lg:text-xl w-[65%] justify-end">
          <Link
            to={withLang(currentLang, SLUGS.us)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.us")}
          </Link>
          <Link
            to={withLang(currentLang, SLUGS.banks)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.banks")}
          </Link>
          <Link
            to={withLang(currentLang, SLUGS.retail)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.retail")}
          </Link>
          <Link
            to={withLang(currentLang, SLUGS.atms)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.atms")}
          </Link>
          <Link
            to={withLang(currentLang, SLUGS.school)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.school")}
          </Link>
          <Link
            to={withLang(currentLang, SLUGS.services)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.services")}
          </Link>
          <Link
            to={withLang(currentLang, SLUGS.blog)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.blog")}
          </Link>
          <Link
            to={withLang(currentLang, SLUGS.contact)}
            className={temaColor ? "nav-text-color" : "nav-text-white"}
          >
            {t("navBar.contact")}
          </Link>
        </div>

        {/* Idioma + iconos */}
        <div className="w-[15%]">
          <div className="p-4">
            <div className="flex items-center justify-center rounded-lg border-orangeAtm shadow-sm">
              <a
                href="https://www.linkedin.com/company/atm-net-system-and-solutions/"
                target="_blank"
                rel="noreferrer"
                className="mr-4"
              >
                <img
                  src={temaColor ? linkedin : linkedinWhite}
                  className="h-8"
                  alt="LinkedIn"
                />
              </a>
              <select
                id="language-select"
                value={language}
                onChange={changeLanguage}
                style={selectStyles}
                className="font-bold text-lg rounded-lg p-2 bg-transparent focus:outline-none"
              >
                <option value="en">{t("language.english")}</option>
                <option value="es">{t("language.spanish")}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Hamburger */}
        <button
          className={
            toggleMenu
              ? "open block hamburger md:hidden focus:outline-none"
              : "block hamburger md:hidden focus:outline-none"
          }
          onClick={() => setToggleMenu(!toggleMenu)}
        >
          <span className="hamburger-top bg-white"></span>
          <span className="hamburger-middle bg-white"></span>
          <span className="hamburger-bottom bg-white"></span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className="md:hidden">
        {toggleMenu && (
          <div className="fixed top-0 left-0 w-full h-screen bg-darkBlueText flex flex-col items-center justify-center space-y-6 font-bold z-50 text-white">
            <button
              onClick={() => setToggleMenu(false)}
              className="absolute top-4 right-4 text-3xl font-bold text-white focus:outline-none"
              aria-label="Cerrar menú"
            >
              ×
            </button>

            <Link
              to={withLang(currentLang, SLUGS.us)}
              className="text-lg"
              onClick={() => setToggleMenu(false)}
            >
              {t("navBar.us")}
            </Link>
            <Link
              to={withLang(currentLang, SLUGS.banks)}
              className="text-lg"
              onClick={() => setToggleMenu(false)}
            >
              {t("navBar.banks")}
            </Link>
            <Link
              to={withLang(currentLang, SLUGS.retail)}
              className="text-lg"
              onClick={() => setToggleMenu(false)}
            >
              {t("navBar.retail")}
            </Link>
            <Link
              to={withLang(currentLang, SLUGS.atms)}
              className="text-lg"
              onClick={() => setToggleMenu(false)}
            >
              {t("navBar.atms")}
            </Link>
            <Link
              to={withLang(currentLang, SLUGS.school)}
              className="text-lg"
              onClick={() => setToggleMenu(false)}
            >
              {t("navBar.school")}
            </Link>
            <Link
              to={withLang(currentLang, SLUGS.services)}
              className="text-lg"
              onClick={() => setToggleMenu(false)}
            >
              {t("navBar.services")}
            </Link>
            <Link
              to={withLang(currentLang, SLUGS.blog)}
              className={temaColor ? "nav-text-color" : "nav-text-white"}
            >
              {t("navBar.blog")}
            </Link>
            <Link
              to={withLang(currentLang, SLUGS.contact)}
              className="text-lg"
              onClick={() => setToggleMenu(false)}
            >
              {t("navBar.contact")}
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};;

export default Navbar;
